import { Field } from "../../field";
import { BusinessOwner, StoreLocation } from "../../fieldsets";
import { Merchant } from "../../merchant";
import { CountryList, State, UiFieldType } from "../../types";

import { determineNonOwnerApplicable, determineOwnerApplicable } from "./utils";

type CountryListBusinessTypeMap = {
  [key in CountryList]: State[];
};

export const countyStateMap: CountryListBusinessTypeMap = {
  Australia: ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"],
  Canada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Yukon",
  ],
  "New Zealand": [],
  "United Kingdom": [],
  "United States": [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "DC",
    "FM",
    "GU",
    "MH",
    "MP",
    "PW",
    "PR",
    "VI",
  ],
};

export class StateField extends Field<string> {
  protected readonly _collection: BusinessOwner | StoreLocation | null;

  constructor(
    model: Merchant,
    collection: BusinessOwner | StoreLocation | null,
    { propertyName }: { propertyName: string }
  ) {
    super(model, {
      dataType: "string",
      propertyName,
      uiInputBaseProps: {
        autoComplete: "address-level1",
      },
    });

    this._collection = collection;
  }

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    const businessType = this._model.companyInformation.businessType.value;

    if (registeredCountry === "United Kingdom") {
      // no state shown in UK
      return false;
    }

    if (this._collection instanceof BusinessOwner) {
      return determineOwnerApplicable(registeredCountry);
    } else if (this._collection instanceof StoreLocation) {
      return true;
    }

    // non owner related (ie company information)
    return determineNonOwnerApplicable(registeredCountry, businessType);
  }

  get testValue(): any {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    if (registeredCountry === "New Zealand") {
      return "Test State";
    }

    return registeredCountry ? countyStateMap[registeredCountry][0] : "AL";
  }

  get uiFieldType(): UiFieldType {
    // NZ only is of type Input
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    if (registeredCountry === "New Zealand") {
      return "Input";
    }
    return "Select";
  }

  get pickList(): State[] {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    return registeredCountry ? countyStateMap[registeredCountry] : [];
  }

  get required(): boolean {
    return this.applicable;
  }

  get shown(): boolean {
    return this.applicable;
  }
}
